import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Close, Download, NoteAdd, Visibility } from "@mui/icons-material";
import {
  DOCUMENT_ENTITY_TYPE_LABELS,
  DOCUMENT_TYPE_LABELS,
} from "@/api/labels";
import {
  useDocument,
  useDownloadVersion,
  useViewVersion,
} from "@/api/hooks/documents";
import { formatBytes, formatDateTime } from "@/utils/format";
import { NewDocumentVersionForm } from "./NewDocumentVersionForm";
import { DocumentValidationsSection } from "./DocumentValidationsSection";
import { DocumentVersionViewer } from "./DocumentVersionViewer";

interface Props {
  open: boolean;
  onClose: () => void;
  documentId: string | null;
  /** Query keys invalidated after a new version is added so the
   * calling table can flip / refresh its rows. */
  invalidateKeys?: (string | undefined)[][];
}

/** Drawer that surfaces a Document's metadata, version history, and
 * (importantly) the "add a new version" flow used by the per-row
 * "View / replace" buttons on the ClinOps site detail. Wider than the
 * upload drawer because it has to fit a version table. */
export function DocumentDetailDrawer({
  open,
  onClose,
  documentId,
  invalidateKeys = [],
}: Props) {
  const qc = useQueryClient();
  const { data: doc, isLoading, error } = useDocument(documentId ?? undefined);
  const download = useDownloadVersion();
  const view = useViewVersion();
  const [addingVersion, setAddingVersion] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [viewer, setViewer] = useState<{
    url: string;
    versionId: string;
  } | null>(null);

  const handleClose = () => {
    setAddingVersion(false);
    setDownloadError(null);
    setViewer(null);
    onClose();
  };

  const handleView = async (versionId: string) => {
    if (!doc) return;
    setDownloadError(null);
    try {
      const res = await view.mutateAsync({ documentId: doc.id, versionId });
      setViewer({ url: res.download_url, versionId });
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : "Could not open viewer");
    }
  };

  const handleDownload = async (versionId: string) => {
    if (!doc) return;
    setDownloadError(null);
    try {
      const res = await download.mutateAsync({ documentId: doc.id, versionId });
      window.location.href = res.download_url;
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : "Download failed");
    }
  };

  const handleVersionAdded = () => {
    setAddingVersion(false);
    for (const key of invalidateKeys) {
      qc.invalidateQueries({ queryKey: key });
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          // Detail drawer hosts the GCP/rotation review panels — those
          // render the doc viewer + form side-by-side, which crams at
          // 720px. Take ~75% of the viewport on desktop with a cap so
          // it doesn't get awkwardly wide on big monitors.
          sx: { width: { xs: "100%", sm: "75vw" }, maxWidth: 1400 },
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            mb: 2,
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {doc ? (
              <>
                <Typography variant="h6" sx={{ wordBreak: "break-word" }}>
                  {doc.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {DOCUMENT_TYPE_LABELS[doc.document_type]} ·{" "}
                  {DOCUMENT_ENTITY_TYPE_LABELS[doc.entity_type]}
                  {doc.deleted_at && (
                    <Chip
                      label="Deleted"
                      color="error"
                      variant="outlined"
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
              </>
            ) : (
              <Typography variant="h6">Document</Typography>
            )}
          </Box>
          <IconButton onClick={handleClose} aria-label="Close" size="small">
            <Close />
          </IconButton>
        </Box>

        {isLoading && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {error && <Alert severity="error">{error.message}</Alert>}

        {doc && (
          <Stack spacing={3}>
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Metadata
              </Typography>
              <Typography variant="body2">
                Created {formatDateTime(doc.created_at)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last updated {formatDateTime(doc.updated_at)}
              </Typography>
              {doc.notes && (
                <Typography
                  variant="body2"
                  sx={{ mt: 1, whiteSpace: "pre-wrap" }}
                >
                  {doc.notes}
                </Typography>
              )}
            </Box>

            {doc.current_version_validations.length > 0 && (
              <>
                <Divider />
                <DocumentValidationsSection
                  documentId={doc.id}
                  validations={doc.current_version_validations}
                  versionsById={Object.fromEntries(
                    doc.versions.map((v) => [v.id, v]),
                  )}
                />
              </>
            )}

            {viewer && (() => {
              const v = doc.versions.find((vv) => vv.id === viewer.versionId);
              if (!v) return null;
              return (
                <>
                  <Divider />
                  <DocumentVersionViewer
                    url={viewer.url}
                    contentType={v.content_type}
                    fileName={v.file_name}
                    versionLabel={`v${v.version_number}`}
                    onClose={() => setViewer(null)}
                  />
                </>
              );
            })()}

            <Divider />

            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="overline" color="text.secondary">
                  Versions · {doc.versions.length}
                </Typography>
                {!doc.deleted_at && !addingVersion && (
                  <Button
                    size="small"
                    startIcon={<NoteAdd />}
                    onClick={() => setAddingVersion(true)}
                  >
                    Add new version
                  </Button>
                )}
              </Box>
              {downloadError && (
                <Alert
                  severity="error"
                  onClose={() => setDownloadError(null)}
                  sx={{ mb: 2 }}
                >
                  {downloadError}
                </Alert>
              )}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Version</TableCell>
                    <TableCell>File</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Uploaded</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {doc.versions.map((v) => (
                    <TableRow key={v.id} hover>
                      <TableCell>
                        v{v.version_number}
                        {v.id === doc.current_version_id && (
                          <Chip
                            label="Current"
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ wordBreak: "break-word", maxWidth: 200 }}>
                        {v.file_name}
                      </TableCell>
                      <TableCell>{formatBytes(v.file_size_bytes)}</TableCell>
                      <TableCell>{formatDateTime(v.uploaded_at)}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleView(v.id)}
                          disabled={view.isPending}
                          aria-label="View"
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(v.id)}
                          disabled={download.isPending}
                          aria-label="Download"
                        >
                          <Download fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {addingVersion && (
              <>
                <Divider />
                <Box>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1 }}
                  >
                    Add new version
                  </Typography>
                  <NewDocumentVersionForm
                    documentId={doc.id}
                    onComplete={handleVersionAdded}
                    onCancel={() => setAddingVersion(false)}
                  />
                </Box>
              </>
            )}
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}
